import * as React from "react";
import {
  Box,
  Column,
  Columns,
  Heading,
  Hidden,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { Field, FieldProps, FormikProps, useFormikContext } from "formik";
import { IFormProps } from "../FormAccordionContainer";
import { capitalize } from "@mui/material";
import { AccountPayer, EnergyType, FormValues } from "../types";
import { GasOptions } from "./GasOptions";
import FormFieldLayout from "./FormFieldLayout";
import { getSmallCase } from "src/components/Premises/helpers";
import { useAppContext } from "src/context/AppContext";
import { InternetRequestStatusEnum } from "../ServiceTypeStatusAlert";

interface IServiceSectionProps extends IFormProps {
  service: EnergyType;
  homeUser?: boolean;
}

export type CollaboratorOption = {
  value: string;
  label: string;
};

const InternetSectionInPremise = (props: IServiceSectionProps) => {
  const { service, homeUser = false } = props;
  const {
    context: { featureFlags },
  } = useAppContext();

  const { values } = useFormikContext<FormValues>();
  const {
    requestingCompany,
    premiseAddress: { state },
  } = values;
  const showGasOptions = state !== "ACT";

  return featureFlags.enablePreinstallAddrNotFound ||
    values.internetRequestStatus === InternetRequestStatusEnum.CAN_RETAIL ? (
    <Stack
      space={{
        xs: "large",
        lg: "xlarge",
      }}
    >
      {values.selectedConnections.length > 1 && (
        <Heading variant="h4">{capitalize(service)}</Heading>
      )}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
      >
        {service === "Gas" && showGasOptions ? <GasOptions /> : null}
        <Columns
          collapseBelow="md"
          space={{
            xs: "xxsmall",
            md: "small",
          }}
          alignY="stretch"
        >
          <Column width="2/3">
            <FormFieldLayout multipleFields>
              <Field id="account-payer" name="internetAccountPayer">
                {({
                  field,
                  meta,
                }: {
                  field: FormikProps<FieldProps>;
                  meta: any;
                }) => (
                  <RadioGroup
                    {...field}
                    id="account-payer"
                    name="internetAccountPayer"
                    label={`Who's responsible for the ${getSmallCase(
                      service
                    )} account?`}
                    error={meta.error}
                    helperText={meta.error}
                  >
                    <Box
                      display="flex"
                      flexWrap="wrap"
                      flexDirection={{
                        xs: "column",
                        sm: "row",
                      }}
                    >
                      <Radio
                        id="my-company"
                        label={
                          requestingCompany
                            ? requestingCompany.name
                            : homeUser
                            ? "Me/My company"
                            : "My company"
                        }
                        value={AccountPayer.COMPANY}
                        helperText="Will be responsible for any charges until the account is closed."
                      />
                      <Radio
                        id="customer"
                        label="Customer"
                        value={AccountPayer.SOMEONE_ELSE}
                        helperText="Will be responsible for any charges."
                      />
                    </Box>
                  </RadioGroup>
                )}
              </Field>
            </FormFieldLayout>
          </Column>
          <Column width="1/3">
            <Box display="flex" alignItems="stretch" height="full">
              <Hidden below="md">
                {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
                <Box
                  style={{
                    borderLeft: "1px solid #E3E3E3",
                    marginLeft: "11px",
                  }}
                  paddingRight="large"
                  height="full"
                />
                {/* End Faux vertical <Divider> */}
              </Hidden>
              <Box alignSelf="center" marginTop="xsmall">
                <Stack
                  space={{
                    xs: "small",
                    md: "medium",
                  }}
                >
                  <Text variant="body-small" tone="neutralLight">
                    The account holder will receive an email to compare our
                    internet plans. There's no obligation for them to sign up
                    with Origin.
                  </Text>
                </Stack>
              </Box>
            </Box>
          </Column>
        </Columns>
      </Stack>
    </Stack>
  ) : null;
};

export default InternetSectionInPremise;
